// 2-step login with an authenticator app (Google / Microsoft Authenticator). Codes are made on the person's phone;
// nothing is sent by SMS. Needs "Firebase Authentication with Identity Platform" (workflow "2-step login").
// Staff login data only, never patient data.
import {
  EmailAuthProvider, getMultiFactorResolver, multiFactor, reauthenticateWithCredential,
  TotpMultiFactorGenerator, type MultiFactorError, type MultiFactorResolver, type TotpSecret, type User,
} from 'firebase/auth';
import { auth } from '../lib/firebase';
import { addAudit } from './auditService';
import { respond } from './respond';
import { isReal } from './store';
import { scope } from './scope';

export const ISSUER = 'Suraksha Sathi';

// Demo only: whether "I" have set it up, kept in this tab.
let demoOn = false;

export function twoStepOn(user: User | null | undefined = auth?.currentUser): boolean {
  if (!isReal()) return demoOn;
  return !!user && multiFactor(user).enrolledFactors.some((f) => f.factorId === TotpMultiFactorGenerator.FACTOR_ID);
}

export interface EnrolStart { qrUrl: string; key: string; secret?: TotpSecret }

/** Step 1: confirm the password, then make a new secret for the authenticator app (shown as a QR code). */
export async function startTwoStep(password: string): Promise<EnrolStart> {
  if (!isReal()) return respond({ qrUrl: 'otpauth://totp/Suraksha%20Sathi:demo%40example.com?secret=DEMODEMODEMODEMO&issuer=Suraksha%20Sathi', key: 'DEMO DEMO DEMO DEMO' }, 300);
  const user = auth!.currentUser;
  if (!user?.email) throw new Error('NOT_SIGNED_IN');
  await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, password));
  const session = await multiFactor(user).getSession();
  const secret = await TotpMultiFactorGenerator.generateSecret(session);
  return { qrUrl: secret.generateQrCodeUrl(user.email, ISSUER), key: secret.secretKey, secret };
}

/** Step 2: the 6-digit code from the app proves it's set up right; then 2-step login is on. */
export async function finishTwoStep(start: EnrolStart, code: string): Promise<void> {
  if (!/^\d{6}$/.test(code.trim())) throw new Error('BAD_CODE');
  if (!isReal()) {
    demoOn = true;
    addAudit(scope().actor, 'user.two_step_on', scope().uid ?? '', scope().facilityId);
    return respond(undefined);
  }
  const user = auth!.currentUser!;
  const assertion = TotpMultiFactorGenerator.assertionForEnrollment(start.secret!, code.trim());
  await multiFactor(user).enroll(assertion, 'Authenticator app');
  addAudit(scope().actor, 'user.two_step_on', user.uid, scope().facilityId);
}

/** Sign-in or unlock needs a code: the resolver Firebase gives us, kept until the code is typed. */
export function resolverFor(err: unknown): MultiFactorResolver | null {
  if ((err as { code?: string })?.code !== 'auth/multi-factor-auth-required' || !auth) return null;
  return getMultiFactorResolver(auth, err as MultiFactorError);
}

/** Finish a sign-in (or unlock) with the 6-digit code from the authenticator app. */
export async function resolveWithCode(resolver: MultiFactorResolver, code: string) {
  const hint = resolver.hints.find((h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID);
  if (!hint) throw new Error('NO_APP_FACTOR');
  if (!/^\d{6}$/.test(code.trim())) throw new Error('BAD_CODE');
  return resolver.resolveSignIn(TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code.trim()));
}
